import { isAxiosError, isCancel } from 'axios'
import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import {
  ChatbotApi,
  ChatbotListItem,
  ChatbotListParams,
  type IParamsGetChatbotList,
  type IResponseChatbotList,
} from 'entities/Chatbot'
import { tableLimitStore } from 'entities/TableLimit'
import { chatbotOperation } from './chatbotOperationStore'

export class ChatbotListStore {
  page = 1
  total = 0
  limit = tableLimitStore.getLimit('chatbot-list-table') ?? 10
  search = ''
  loading = true
  loadingScheduled = false
  initialLoading = true

  private _listMap = new Map<number, ChatbotListItem>()
  private _updatingIds = new Set<number>()
  private _hasRetriedAtMinLimit = false
  private _isRetrying = false

  constructor() {
    makeAutoObservable(this)

    reaction(
      () => this._chatbotListRequestParams,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get isEmpty() {
    if (this.initialLoading || this.loadingScheduled || this.loading || !!this.search.length)
      return false

    return !this._listMap.size
  }

  get noData() {
    if (this.initialLoading || this.loadingScheduled || this.loading) return false

    return !!this.search.length && !this._listMap.size
  }

  setSearch = (value: string) => {
    if (this.search === value) return

    this.page = 1
    this.search = value
    this.loadingScheduled = true
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this.loading = true
      })

      try {
        const { data } = await ChatbotApi.getChatbotList(this._chatbotListRequestParams, {
          cancelToken: cancelRequest.token,
        })

        this._setData(data)

        runInAction(() => {
          this.loading = false
          this.initialLoading = false
          this.loadingScheduled = false
          this._hasRetriedAtMinLimit = false
        })
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: chatbot-list-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this.limit
          const newLimit = tableLimitStore.reduceLimit('chatbot-list-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
              this._isRetrying = true
              this.page = 1
              this.limit = newLimit
            })
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
              this._isRetrying = true
            })
            this._requestLoadData.debounceLoadData()
          } else {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
            })
            throw error
          }
        } else {
          throw error
        }
      }
    },
    errorCallback: (error) => {
      if (!isCancel(error)) {
        runInAction(() => {
          this.loading = false
          this.loadingScheduled = false
          this.initialLoading = false
        })
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        if (!this._isRetrying) {
          this.loading = false
          this.loadingScheduled = false
          this.initialLoading = false
        }
        this._isRetrying = false
      })
    },
  })

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  updateStatus = async (id: number, isActive: boolean) => {
    if (this._updatingIds.has(id)) return
    this._updatingIds.add(id)

    try {
      const { data } = (await chatbotOperation.updateStatus(id, isActive)) ?? {}

      runInAction(() => {
        if (!data) return

        this._listMap.get(data.id)?.syncOrigin(data)
      })
    } catch (error) {
      logger.error(error)
    } finally {
      this._updatingIds.delete(id)
    }
  }

  deleteRow = (id: number) =>
    chatbotOperation.delete(id).then((deleted) => {
      if (deleted) this.loadData()
    })

  changePaging = (page: number, limit: number) => {
    if (this.page === page && this.limit === limit) return

    this.page = page
    this.limit = limit
    this.loadingScheduled = true
    tableLimitStore.setLimit('chatbot-list-table', limit)
  }

  dispose = () => {
    this._requestLoadData.dispose()
  }

  private get _chatbotListRequestParams(): IParamsGetChatbotList {
    return {
      page: this.page,
      limit: this.limit,
      [ChatbotListParams.Name]: this.search,
    }
  }

  private _setData = ({ data, meta }: IResponseChatbotList) => {
    this._listMap.clear()
    data.forEach((item) => this._listMap.set(item.id, new ChatbotListItem(item)))

    this.page = meta.current_page
    this.total = meta.total
  }
}
